import React from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from './Reveal';
import { Target, Eye, ShieldCheck, Clock, Link as LinkIcon } from 'lucide-react';
import { NAV_ITEMS } from '../constants';

export const AboutSection: React.FC = () => {
    return (
        <section id="about" className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4">
                <Reveal>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">About Villa Avoils</h2>
                        <div className="h-1 w-20 bg-avo-gold mx-auto rounded-full"></div>
                    </div>
                </Reveal>

                <div className="flex flex-col lg:flex-row gap-16 items-start mb-16">
                    <div className="lg:w-1/2">
                        <Reveal>
                            <h3 className="text-2xl font-heading font-bold text-avo-forest mb-6">Leading Avocado Oil Company in Nyeri</h3>
                            <p className="text-gray-700 text-lg leading-relaxed mb-6">
                                <strong>Villa Avoils International Limited</strong> is a fully integrated agribusiness ecosystem focused on value addition. Situated in the heart of Nyeri, Muruguru, Kenya, we have revolutionized the avocado value chain by bridging the gap between traditional small-scale farming and modern industrial processing.
                            </p>
                            <p className="text-gray-700 text-lg leading-relaxed mb-6">
                                Our operations encompass the extraction of premium Cold Press Crude Avocado Oil. This model ensures <strong>zero waste</strong>, maximizes resource efficiency, and guarantees sustainability at every step.
                            </p>
                            <p className="text-gray-700 text-lg leading-relaxed">
                                We are driven by a commitment to <strong>farmer empowerment</strong>. By working directly with local farmers, we provide a reliable market for their produce, fair prices, and agronomic support, ensuring that our growth contributes directly to the prosperity of our community.
                            </p>
                        </Reveal>
                    </div>
                    <div className="lg:w-1/2 grid grid-cols-1 gap-6">
                         <Reveal delay={100}>
                             <div className="bg-stone-50 p-8 rounded-xl border border-stone-100 hover:shadow-md transition-shadow">
                                <Target className="text-avo-gold mb-4" size={32} />
                                <h4 className="font-bold text-xl mb-2 text-avo-forest">Our Mission</h4>
                                <p className="text-gray-700">To produce premium, nutrient-dense avocado products through innovative, eco-friendly processes that promote holistic health, ensure quality control, and economically uplift our farming partner communities.</p>
                             </div>
                         </Reveal>
                         <Reveal delay={200}>
                             <div className="bg-stone-50 p-8 rounded-xl border border-stone-100 hover:shadow-md transition-shadow">
                                <Eye className="text-avo-gold mb-4" size={32} />
                                <h4 className="font-bold text-xl mb-2 text-avo-forest">Our Vision</h4>
                                <p className="text-gray-700">To be the global benchmark for sustainable avocado value addition, creating a zero-waste future where agriculture regenerates the land and fuels long-term growth.</p>
                             </div>
                         </Reveal>
                         <Reveal delay={300}>
                             <div className="bg-stone-50 p-8 rounded-xl border border-stone-100 hover:shadow-md transition-shadow">
                                <ShieldCheck className="text-avo-gold mb-4" size={32} />
                                <h4 className="font-bold text-xl mb-2 text-avo-forest">Core Values</h4>
                                <p className="text-gray-700">Sustainability, Innovation, Integrity, Quality Excellence, and Community Partnership.</p>
                             </div>
                         </Reveal>
                    </div>
                </div>

                {/* Quick Links & Business Hours */}
                <Reveal delay={400}>
                    <div className="grid md:grid-cols-2 gap-8 bg-stone-50 p-8 rounded-2xl border border-stone-100">
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-avo-forest/10 rounded-full flex items-center justify-center text-avo-forest">
                                    <LinkIcon size={20} />
                                </div>
                                <h4 className="text-xl font-bold font-heading text-gray-900">Quick Links</h4>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {NAV_ITEMS.map((item) => (
                                    <Link 
                                        key={item.id}
                                        to={item.path || '/'}
                                        className="text-left text-gray-600 hover:text-avo-forest transition-colors font-medium"
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-avo-forest/10 rounded-full flex items-center justify-center text-avo-forest">
                                    <Clock size={20} />
                                </div>
                                <h4 className="text-xl font-bold font-heading text-gray-900">Business Hours</h4>
                            </div>
                            <ul className="space-y-3 text-gray-600">
                                <li className="flex justify-between border-b border-stone-200 pb-2">
                                    <span>Monday - Friday:</span>
                                    <span className="font-medium text-gray-900">8:00 AM - 5:00 PM</span>
                                </li>
                                <li className="flex justify-between border-b border-stone-200 pb-2">
                                    <span>Saturday:</span>
                                    <span className="font-medium text-gray-900">9:00 AM - 1:00 PM</span>
                                </li>
                                <li className="flex justify-between">
                                    <span>Sunday & Public Holidays:</span>
                                    <span className="font-medium text-gray-900">Closed</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
